// ============================================================================
// APPROVAL HOOKS
// ============================================================================
// React hooks for the Approval Inbox.

import { useCallback, useEffect, useState } from 'react';
import { approvalService } from 'api/services/approvalService';
import type { Approval, ApprovalDecisionRequest, ApprovalSearchRequest } from 'api/types/approval.types';
import type { ID } from 'api/types';

/** List approvals with auto-fetch on filter/page change. */
export const useApprovals = (params?: ApprovalSearchRequest) => {
  const [data, setData] = useState<Approval[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 25,
    totalPages: 0,
    totalItems: 0,
    hasNextPage: false,
    hasPreviousPage: false,
  });

  const paramsKey = JSON.stringify(params);

  const fetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await approvalService.getAll(params);
      setData(response.data);
      setPagination(response.pagination);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey]);

  useEffect(() => { fetch(); }, [fetch]);

  return { data, loading, error, pagination, refetch: fetch };
};

/** One approval, on demand (e.g. when opening the action modal). */
export const useApproval = () => {
  const [data, setData] = useState<Approval | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const get = useCallback(async (id: ID) => {
    setLoading(true);
    setError(null);
    try {
      const response = await approvalService.getById(id);
      setData(response.data);
      return response.data;
    } catch (err) {
      setError(err as Error);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { data, loading, error, get };
};

/** Mutation hooks (no pre-fetch, returns an `act` callback + loading flag). */
export const useApproveAction = () => {
  const [loading, setLoading] = useState(false);
  const act = useCallback(async (id: ID, body: ApprovalDecisionRequest) => {
    setLoading(true);
    try {
      const response = await approvalService.approve(id, body);
      return response.data;
    } finally {
      setLoading(false);
    }
  }, []);
  return { act, loading };
};

export const useRejectAction = () => {
  const [loading, setLoading] = useState(false);
  const act = useCallback(async (id: ID, body: ApprovalDecisionRequest) => {
    setLoading(true);
    try {
      const response = await approvalService.reject(id, body);
      return response.data;
    } finally {
      setLoading(false);
    }
  }, []);
  return { act, loading };
};
